import { useEffect, useLayoutEffect, useState, type ReactNode } from "react";

import { cn } from "@/lib/utils";
import { useAppBootstrap } from "@/context/AppBootstrapContext";
import { NavRailPreviewProvider } from "@/context/NavRailPreviewContext";
import { OrganizationProvider } from "@/context/OrganizationContext";
import { TopBarSubtitleProvider } from "@/context/TopBarSubtitleContext";
import { RightRailProvider, useRightRail } from "@/context/RightRailContext";
import {
  KaiChatProvider,
} from "@/features/kai/KaiChatContext";
import { useAppViewport } from "@/hooks/useAppViewport";
import { useFeedPageKaiRail } from "@/hooks/useFeedPageKaiRail";
import { useGlassHoverGlow } from "@/hooks/useGlassHoverGlow";
import { useRightRailResize } from "@/hooks/useRightRailResize";
import { useMobileNavInset, useTopBarInset } from "@/hooks/useTopBarInset";
import { BackgroundLayer } from "./BackgroundLayer";
import { GraphMailboxBanner } from "./GraphMailboxBanner";
import { TopBar } from "./TopBar";
import { IconRail } from "./IconRail";
import { MobileNav } from "./MobileNav";
import { MobileWorkspaceSheet } from "./MobileWorkspaceSheet";
import { NotificationsPanel } from "./NotificationsPanel";
import { RightRail } from "./RightRail";
import { RightRailResizeHandle } from "./RightRailResizeHandle";
import { PreferencesSheet } from "@/features/preferences/PreferencesSheet";
import { GlobalSearchDialog } from "@/features/search/GlobalSearchDialog";

interface AppShellProps {
  children: ReactNode;
  /** Hide the desktop right rail content (Kai landing, focused feed page). */
  hideRightRail?: boolean;
  /** Kai chat owns the main column scroll — lock `main-scroll` overflow. */
  kaiPageLock?: boolean;
  /** Desktop feed: Kai + Filters + collapsed Notes, never the notification stack. */
  kaiOnlyRail?: boolean;
  /** Table list pages: column starts below the pills with a flush sticky header. */
  noTopScrollMask?: boolean;
}

function AppShellFrame({
  children,
  hideRightRail,
  kaiPageLock = false,
  kaiOnlyRail = false,
  noTopScrollMask = false,
}: AppShellProps) {
  useAppViewport();
  useGlassHoverGlow();
  const topbarRef = useTopBarInset<HTMLDivElement>();
  const mobileNavRef = useMobileNavInset<HTMLDivElement>();
  const {
    notificationsPanelActive,
    closeNotifications,
    mobileRailOpen,
    mobileRailMode,
    setMobileRailOpen,
  } = useRightRail();
  const showKaiOnlyRail = kaiOnlyRail;
  const showRail = !hideRightRail || showKaiOnlyRail;
  useFeedPageKaiRail(kaiOnlyRail);
  const { onResizePointerDown, resetWidth } = useRightRailResize({
    resizable: showRail,
  });

  useEffect(() => {
    if (!kaiOnlyRail) return;
    if (notificationsPanelActive) closeNotifications();
    if (mobileRailOpen && mobileRailMode === "notifications") {
      setMobileRailOpen(false);
    }
  }, [
    kaiOnlyRail,
    notificationsPanelActive,
    closeNotifications,
    mobileRailOpen,
    mobileRailMode,
    setMobileRailOpen,
  ]);

  const { phase } = useAppBootstrap();
  const isBootPending = phase === "splash";
  const isShellVisible = phase === "exiting" || phase === "ready";
  // Paint the sliver after the chrome becomes visible, then start the open
  // animation on the following frame. iOS WebKit skips clip-path / opacity
  // motion that begins in the same frame as `visibility: hidden → visible`.
  const [bootReveal, setBootReveal] = useState(false);

  useLayoutEffect(() => {
    if (phase === "splash") {
      setBootReveal(false);
      return;
    }
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setBootReveal(true);
      return;
    }
    let frame2 = 0;
    const frame1 = requestAnimationFrame(() => {
      frame2 = requestAnimationFrame(() => setBootReveal(true));
    });
    return () => {
      cancelAnimationFrame(frame1);
      if (frame2) cancelAnimationFrame(frame2);
    };
  }, [phase]);

  return (
    <div
      className={cn(
        /* Mobile: horizontal padding only — top inset + zero bottom come from
           globals.css `.shell-root` so the nav can sit flush on the home indicator. */
        "shell-root relative flex flex-col overflow-hidden px-[var(--shell-padding)] md:p-[var(--shell-padding)]",
        "fixed inset-x-0 top-[var(--app-vv-offset-top,0px)] z-0 md:inset-x-0 md:top-0",
        isBootPending && "shell-boot-pending",
        isShellVisible && "shell-boot-visible",
        bootReveal && "shell-boot-reveal",
      )}
    >
      <BackgroundLayer />

      <div
        ref={topbarRef}
        className="shell-enter-topbar pointer-events-none absolute inset-x-0 top-[var(--shell-top-inset)] z-30 flex justify-center px-[var(--shell-padding)]"
      >
        <div className="pointer-events-auto">
          <TopBar />
        </div>
      </div>

      <div
        className="pointer-events-none absolute top-1/2 z-50 hidden -translate-y-1/2 overflow-visible md:flex md:justify-center"
        style={{
          left: "var(--shell-padding)",
          width: "var(--rail-gutter-width)",
        }}
      >
        <div className="shell-enter-rail pointer-events-auto overflow-visible">
          <IconRail />
        </div>
      </div>

      {/* Body + mobile nav share the shell column — nav overlays the scrollport. */}
      <div className="relative z-0 flex min-h-0 flex-1 flex-col overflow-hidden">
        <div
          aria-hidden
          className="shell-scroll-mask shell-scroll-mask--top md:hidden"
        />
        <div
          aria-hidden
          className="shell-scroll-mask shell-scroll-mask--bottom md:hidden"
        />
        <div className="flex min-h-0 flex-1 overflow-hidden gap-[var(--shell-gap)]">
          <aside
            aria-hidden="true"
            className="hidden shrink-0 md:block"
            style={{ width: "var(--rail-gutter-width)" }}
          />

          <div
            className={cn(
              "main-scroll-frame scroll-mask-edges relative flex min-h-0 min-w-0 flex-1 flex-col",
              kaiPageLock && "main-scroll-frame--locked",
              noTopScrollMask && "main-scroll-frame--flush-head",
            )}
          >
            <main
              className={cn(
                "main-scroll relative flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden overscroll-y-contain",
                isBootPending || kaiPageLock ? "overflow-y-hidden" : "overflow-y-auto",
                kaiPageLock && "items-center",
                kaiPageLock && "main-scroll--kai",
              )}
            >
              {!hideRightRail && <GraphMailboxBanner />}
              {children}
              {!kaiPageLock && (
                <div className="main-scroll-bottom-spacer" aria-hidden />
              )}
            </main>
          </div>

          {showRail && (
            <aside
              aria-label={showKaiOnlyRail ? "Kai" : "Workspace gutter"}
              className="shell-enter-rail-right right-rail-inset relative hidden min-h-0 min-w-0 shrink-0 self-stretch overflow-visible lg:flex lg:w-[var(--right-rail-width)] lg:max-w-[var(--right-rail-width)] lg:flex-col"
            >
              <RightRailResizeHandle
                onPointerDown={onResizePointerDown}
                onDoubleClick={resetWidth}
              />
              <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
                <RightRail className="h-full w-full" kaiOnly={showKaiOnlyRail} />
              </div>
            </aside>
          )}
        </div>

        <div
          ref={mobileNavRef}
          className="shell-enter-mobile shell-mobile-nav-dock shrink-0 md:hidden"
        >
          <MobileNav />
        </div>
      </div>

      {kaiPageLock && notificationsPanelActive && (
        <aside
          aria-label="Notifications"
          className="shell-enter-rail-right pointer-events-none absolute z-20 hidden min-h-0 min-w-0 flex-col overflow-hidden lg:flex"
          style={{
            right: "var(--shell-padding)",
            top: "calc(var(--shell-padding) + var(--main-scroll-top))",
            bottom: "var(--shell-padding)",
            width:
              "min(var(--right-rail-width), calc(var(--app-vvw, 100%) - var(--shell-padding) * 2))",
            maxWidth:
              "calc(var(--app-vvw, 100%) - var(--shell-padding) * 2)",
          }}
        >
          <div className="pointer-events-auto flex min-h-0 min-w-0 max-w-full flex-1 flex-col overflow-hidden">
            <NotificationsPanel
              fill
              className="flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col"
            />
          </div>
        </aside>
      )}

      <MobileWorkspaceSheet kaiPage={kaiPageLock} />
      <PreferencesSheet />
      <GlobalSearchDialog />
    </div>
  );
}

export function AppShell(props: AppShellProps) {
  return (
    <OrganizationProvider>
      <TopBarSubtitleProvider>
        <RightRailProvider>
          <NavRailPreviewProvider>
            <KaiChatProvider>
              <AppShellFrame {...props} />
            </KaiChatProvider>
          </NavRailPreviewProvider>
        </RightRailProvider>
      </TopBarSubtitleProvider>
    </OrganizationProvider>
  );
}
